import * as React from "react";
import { useContext } from "react";
import { AvailableLanguagesResponse, LanguageInfo } from "./LanguageSelector";
import axios from "axios";
import { FormsLocale, FormsLocaleProvider } from "@tlc/forms/FormsLocale";
import { createAsyncCache } from "../util/createAsyncCache";
import { createKeyedAsyncCache } from "../util/createKeyedAsyncCache";

export interface Translations {
  [key: string]: string;
}

type LanguageContextType = {
  translate: (key: string, params?: { [key: string]: string }) => string;
  translateToken: (token: string, params?: { [key: string]: string }) => string;
  availableLanguages: LanguageInfo[];
  changeLanguageCode: (code: LanguageInfo["code"]) => void;
  languageCode: LanguageInfo["code"];
  localizedShortDateFormat: string;
  dictionary: Translations;
};

export interface Language {
  languageInfo: LanguageInfo;
  dictionary: Translations;
}

const LanguageContext = React.createContext<LanguageContextType>(null);

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
}

type LanguageProviderProps = {};

type IntlConstructorWithSupportedLocales = {
  supportedLocalesOf(
    locales: string | readonly string[],
    options?: { localeMatcher?: "lookup" | "best fit" },
  ): string[];
};

const isSupportedBy = (
  constructor: IntlConstructorWithSupportedLocales,
  locale: string,
) => {
  return (
    constructor.supportedLocalesOf(locale, { localeMatcher: "lookup" }).length >
    0
  );
};

const isSupportedLocale = (languageCode: string): boolean => {
  const normalized = languageCode.replaceAll("_", "-"); // replace _ with - to conform to BCP 47
  try {
    const locale = new Intl.Locale(normalized);
    const localeString = locale.toString();
    const supported =
      isSupportedBy(Intl.Collator, localeString) ||
      isSupportedBy(Intl.DateTimeFormat, localeString) ||
      isSupportedBy(Intl.DisplayNames, localeString) ||
      isSupportedBy(Intl.ListFormat, localeString) ||
      isSupportedBy(Intl.NumberFormat, localeString) ||
      isSupportedBy(Intl.PluralRules, localeString) ||
      isSupportedBy(Intl.RelativeTimeFormat, localeString);
    if (!supported) {
      console.warn(
        `Locale ${localeString} is not supported by this browser. This locale will not be available for use.`,
      );
    }
    return supported;
  } catch (e) {
    console.warn(
      `Invalid locale syntax ${normalized}. This locale will not be available for use. Error:`,
      e,
    );
    return false;
  }
};

function getLocaleMatchScore(
  requested: Intl.Locale,
  available: Intl.Locale,
): number {
  if (requested.language !== available.language) {
    return 0;
  }

  let score = 10; // same language

  // only boost if the requested locale explicitly specifies a script
  if (requested.script && available.script === requested.script) {
    score += 4;
  }

  // only boost if the requested locale explicitly specifies a region
  if (requested.region && available.region === requested.region) {
    score += 3;
  } else if (!requested.region && available.region === "US") {
    // if it does not specify a region, prefer US region as fallback if one is not specified (given our customer base)
    // e.g. "en" should prefer "en-US" over "en-GB" but not over "en"
    // "es" should prefer "es-US" over "es-ES" and "es-MX" but not over "es"
    score += 2;
  }

  // Exact locale parts match, including any parts that are not specified
  // e.g. "en" would be an exact match with "en" because script and region are not specified in either
  // This would give "en" a score of 20, "en_US" a score of 12, and "en_GB" a score of 10 when the requested locale is "en"
  if (
    requested.language === available.language &&
    requested.script === available.script &&
    requested.region === available.region
  ) {
    score += 10;
  }

  return score;
}

const defaultLanguageInfoCode = "en_US";

const getLocaleMatchCodeFromBCP47Tag = (
  availableLanguages: LanguageInfo[],
): LanguageInfo["code"] => {
  try {
    const documentLocale = new Intl.Locale(document.documentElement.lang);

    const scoredLanguages = availableLanguages.map((li) => {
      try {
        const languageTag = li.code.replaceAll("_", "-"); // replace _ with - to conform to BCP 47
        return {
          score: getLocaleMatchScore(
            documentLocale,
            new Intl.Locale(languageTag),
          ),
          code: li.code,
        };
      } catch (e) {
        console.warn(
          `Invalid locale syntax ${li.code} in available languages. This language will be ignored. Error:`,
          e,
        );
        return { score: 0, code: li.code };
      }
    });
    const bestMatch = scoredLanguages.reduce((prev, current) => {
      return prev.score > current.score ? prev : current;
    });
    if (bestMatch.score > 0) {
      return bestMatch.code;
    }
    console.warn(
      `No available languages match the document locale ${documentLocale.toString()}. Falling back to default language ${defaultLanguageInfoCode}.`,
    );
    return defaultLanguageInfoCode;
  } catch (e: unknown) {
    console.warn(
      `Invalid locale syntax ${document.documentElement.lang} on html element. Falling back to default language ${defaultLanguageInfoCode}. Error:`,
      e,
    );
    return defaultLanguageInfoCode;
  }
};

type LanguageSubscriber = (
  language: Language,
  availableLanguages: LanguageInfo[],
) => void;

export const LanguageHolder = (() => {
  class LanguageService {
    private _languageCode: string = null;
    private subscribers = new Set<LanguageSubscriber>();

    get dictionary() {
      if (!this._languageCode) {
        return null;
      }
      return (
        this.languageCache.getCurrent(this._languageCode)?.dictionary ?? null
      );
    }

    get languageCode() {
      return this._languageCode;
    }

    private availableLanguagesCache = createAsyncCache<LanguageInfo[]>(
      async () => {
        const { data } = await axios.get<AvailableLanguagesResponse>(
          "/languages",
        );

        return data.availableLanguages.filter((li) =>
          isSupportedLocale(li.code),
        );
      },
    );

    async getAvailableLanguages(): Promise<LanguageInfo[]> {
      return this.availableLanguagesCache.get();
    }

    get availableLanguages() {
      return this.availableLanguagesCache.current ?? null;
    }

    private languageCache = createKeyedAsyncCache<
      LanguageInfo["code"],
      Language
    >(async (languageCode) => {
      const { data } = await axios.get<Language>(`/language/${languageCode}`, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        params: {
          _t: new Date().getTime(), // Add a timestamp to the request to make it unique
        },
      });
      return data;
    });

    private notify(language: Language, availableLanguages: LanguageInfo[]) {
      this.subscribers.forEach((sub) =>
        sub({ ...language }, availableLanguages),
      );
    }

    async fetchLanguage(forceUpdate: boolean = false) {
      const availableLanguages = await this.getAvailableLanguages();

      if (!this._languageCode) {
        this._languageCode = getLocaleMatchCodeFromBCP47Tag(availableLanguages);
      }

      const language = await this.languageCache.get(
        this.languageCode,
        forceUpdate,
      );

      this.notify(language, availableLanguages);

      return language;
    }

    public subscribe(
      subscriber: (
        language: Language,
        availableLanguages: LanguageInfo[],
      ) => void,
    ) {
      this.subscribers.add(subscriber);

      void this.fetchLanguage();

      return () => {
        this.subscribers.delete(subscriber);
      };
    }

    public async updateLanguageCode(
      newLanguageCode: LanguageInfo["code"],
    ): Promise<void> {
      if (this._languageCode !== newLanguageCode) {
        try {
          const locale = new Intl.Locale(newLanguageCode.replaceAll("_", "-")); // validate the new language code, replacing _ with - to conform to BCP 47
          document.documentElement.lang = locale.toString();
          this._languageCode = newLanguageCode;
          await this.fetchLanguage(true);
        } catch (e: unknown) {
          console.warn(
            `Attempted to set invalid locale ${newLanguageCode} on html element. Error:`,
            e,
          );
          return;
        }
      }
      return;
    }
  }

  return new LanguageService();
})();

export function LanguageProvider({
  children,
}: React.PropsWithChildren<LanguageProviderProps>) {
  const [dictionary, setDictionary] = React.useState<Translations>(
    LanguageHolder.dictionary,
  );
  const [availableLanguages, setAvailableLanguages] = React.useState<
    LanguageInfo[]
  >(LanguageHolder.availableLanguages);
  const [languageCode, setLanguageCode] = React.useState<LanguageInfo["code"]>(
    LanguageHolder.languageCode,
  );

  React.useEffect(() => {
    const unsubscribeLanguageHolder = LanguageHolder.subscribe(
      (language, availableLanguages) => {
        setDictionary(language.dictionary);
        setLanguageCode(language.languageInfo.code);
        setAvailableLanguages(availableLanguages);
      },
    );

    return () => {
      unsubscribeLanguageHolder();
    };
  }, []);

  const translate = React.useCallback(
    (key: string, params?: { [key: string]: string }): string => {
      let translation = (dictionary && dictionary[key]) || key; // Fallback to key if tranlation not found

      if (params) {
        Object.keys(params).forEach((placeholder) => {
          const regex = new RegExp(`%${placeholder}%`, "g");
          translation = translation.replace(regex, params[placeholder]);
        });
      }

      return translation;
    },
    [dictionary],
  );

  const localizedShortDateFormat = React.useMemo(() => {
    const key = "localizedShortDateFormatOverride";
    if (dictionary && dictionary[key]) {
      return dictionary[key];
    }
    return "L"; // https://day.js.org/docs/en/display/format#list-of-localized-formats
  }, [dictionary]);

  const translateToken = React.useCallback(
    (token: string, params?: { [key: string]: string }): string => {
      return translate(token.replace(/</g, "").replace(/>/g, ""), params);
    },
    [translate],
  );

  async function changeLanguageCode(code: LanguageInfo["code"]): Promise<void> {
    // check against available languages?
    setLanguageCode(code);
    await LanguageHolder.updateLanguageCode(code);
    // wait for the new language to be set on the backend then refresh the page
    window.location.reload(); // once everything is in React, we won't need to refresh
  }

  const locale: FormsLocale = React.useMemo(() => {
    const formsLocale: FormsLocale = {
      checkboxGroup: {
        selectAll: translate("selectAll"),
        selectedDescription: function (
          checkedCount: number,
          totalCount: number,
        ): string {
          return `${checkedCount} ${translate("of")} ${totalCount} ${translate(
            "selected",
          )}`;
        },
        required: function (
          label?: React.ReactNode,
          singularLabel?: string,
        ): string {
          if (singularLabel) {
            return translate("atLeastOneRequired", {
              SINGULAR_LABEL: singularLabel,
            });
          }
          if (typeof label === "string") {
            return translate("atLeastOneRequired", {
              SINGULAR_LABEL: label,
            });
          }
          return translate("atLeastOneFieldRequired");
        },
      },
      singleSelect: {
        required: (label?: React.ReactNode) => {
          return typeof label === "string"
            ? translate("isRequired", { LABEL: label })
            : translate("requiredFieldError");
        },
      },
      dialog: {
        cancel: translate("cancel"),
        save: translate("save"),
        sessionTimeout: {
          headline: translate("sessionTimeoutWarning_Headline"),
          body: translate("sessionTimeoutWarning_Body"),
          confirmButton: translate("sessionTimeoutWarning_StayLoggedIn"),
          dismissButton: translate("sessionTimeoutWarning_Logout"),
        },
        networkError: {
          headline: translate("networkErrorTitle"),
          body: translate("networkErrorMessage"),
          showDetails: translate("networkErrorShowDetails"),
          confirmButton: translate("networkErrorRetry"),
        },
        genericError: {
          headline: translate("genericErrorTitle"),
          body: translate("genericErrorMessage"),
          showDetails: translate("genericErrorShowDetails"),
          confirmButton: translate("genericErrorConfirmButton"),
        },
      },
      formTextField: {
        required: function (label: string): string {
          return `${translate("isRequired", {
            LABEL: label,
          })}`;
        },
        maxLength: function (label: string, maxLength: number): string {
          return `${translate("maxLengthError", {
            LABEL: label,
            MAX_LENGTH: maxLength.toString(),
          })}`;
        },
      },
    };
    return formsLocale;
  }, [translateToken, translate]);

  const contextValue = {
    translate,
    translateToken,
    availableLanguages,
    changeLanguageCode,
    languageCode,
    localizedShortDateFormat,
    dictionary,
  };

  return (
    <FormsLocaleProvider locale={locale}>
      <LanguageContext.Provider value={contextValue}>
        {children}
      </LanguageContext.Provider>
    </FormsLocaleProvider>
  );
}
